import React, { useRef, useEffect, useState } from "react"; // useState をインポート
import { Group, Rect, Text, Transformer, Image as KonvaImage } from "react-konva"; // Image を KonvaImageとしてインポート

// --- 定数定義 ---
// グリッドサイズ
const GRID_SIZE = 25; // FloorPlanner.jsx と同じ値を使用
// 帖数計算の係数 (1平米あたり約0.605帖、1.653平米あたり1帖)
// 面積計算の単位変換係数 (mm^2 -> m^2)
// const AREA_UNIT_FACTOR = 10000; // この定数は新しい計算では不要

// グリッド線の間隔 (FloorPlanner.jsx と同じ値を使用)
// この50 Konva単位が910mmに相当すると定義
const GRID_LINE_SIZE = 50; // FloorPlanner.jsx で定義されている値
const MM_PER_GRID_LINE_SIZE = 910; // 50 Konva単位 = 910mm

// Konva単位からmmへの変換係数
const KONVA_UNIT_TO_MM = MM_PER_GRID_LINE_SIZE / GRID_LINE_SIZE; // 910 / 50 = 18.2

// 帖数計算の基準 (1帖 = 1.6562 平米) - 一般的な不動産表記に合わせる
const SQ_METERS_PER_JOU = 1.6562;


const Room = ({ shapeProps, isSelected, onSelect, onChange, isFreeSize = false }) => {
  const groupRef = useRef();
  const trRef = useRef();
  const [imageToDisplay, setImageToDisplay] = useState(null); // 表示するImageオブジェクト用のstate
  // グリッドにスナップするヘルパー関数
  const snapToGrid = (value) => isFreeSize ? value : Math.round(value / GRID_SIZE) * GRID_SIZE;

  useEffect(() => {
    if (isSelected && groupRef.current && trRef.current) {
      // Transformer の対象ノードを設定
      trRef.current.nodes([groupRef.current]);
      // レイヤーを再描画して Transformer を表示
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  // shapeProps からプロパティを分割代入 (id, fill, image も受け取る想定)
  const { id, x, y, width, height, name, rotation = 0, fill, image: imageSrc } = shapeProps;

  // 画像の読み込み処理
  useEffect(() => {
    if ((name.startsWith("浴室") || name.startsWith("階段")) && imageSrc) { // ★ 条件に階段を追加
      const img = new window.Image();
      img.src = imageSrc;
      img.onload = () => {
        setImageToDisplay(img);
      };
      // エラーハンドリングは省略 (必要に応じて追加)
    } else {
      setImageToDisplay(null); // 該当しない、または画像がない場合はクリア
    }
  }, [name, imageSrc]); // name または imageSrc が変更されたら再実行

  // テキスト表示用の帖数計算
  // width, height は Konva単位
  // Konva単位^2 -> mm^2 -> m^2 -> 帖 と変換
  const areaInJou = (width * height) * (KONVA_UNIT_TO_MM * KONVA_UNIT_TO_MM) / (1000 * 1000) / SQ_METERS_PER_JOU;

  return (
    <>
      <Group
        ref={groupRef}
        // Konvaノードのプロパティとして shapeProps の値を展開
        id={id}
        x={x}
        y={y}
        width={width}
        height={height}
        rotation={rotation}
        draggable // ドラッグ可能にする
        onClick={onSelect} // クリックで選択
        onTap={onSelect} // タップで選択
        dragBoundFunc={(pos) => ({
          // ドラッグ範囲をグリッドに制限
          x: snapToGrid(pos.x),
          y: snapToGrid(pos.y),
        })}
        onDragEnd={(e) => {
          // ドラッグ終了時に新しい座標で状態を更新
          onChange({
            ...shapeProps,
            x: snapToGrid(e.target.x()),
            y: snapToGrid(e.target.y()),
          });
        }}
        onTransformEnd={(e) => {
          // 変形終了時の処理
          const node = groupRef.current;
          if (!node) return;

          const tr = trRef.current;
          // どのアンカー（ハンドル）が操作されたかを取得
          const activeAnchor = tr?.getActiveAnchor?.();

          // 変形後のスケールを取得
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();

          // ★重要：スケールを元に戻す（これをしないと次回以降の計算が狂う）
          node.scaleX(1);
          node.scaleY(1);

          // 変形後の「見た目上の」サイズを計算
          // node.width() や node.height() は元の値なので shapeProps の width/height を使う
          const visualWidth = width * scaleX;
          const visualHeight = height * scaleY;

          // グリッドにスナップした新しい幅と高さを計算
          // Math.max で最小サイズを GRID_SIZE に制限
          const newWidth = Math.max(snapToGrid(visualWidth), GRID_SIZE);
          const newHeight = Math.max(snapToGrid(visualHeight), GRID_SIZE);

          // --- 対角固定のための新しい座標計算 ---
          // デフォルトは Transformer が変形中に変更した node の x, y を使う
          let newX = node.x();
          let newY = node.y();

          // 変形前の座標とサイズ (計算の基準にする)
          const prevX = shapeProps.x;
          const prevY = shapeProps.y;
          const prevWidth = shapeProps.width;
          const prevHeight = shapeProps.height;

          // 回転がない場合のみ、アンカーに応じた座標調整を行う
          // 回転がある場合の対角固定は計算が複雑になるため、ここでは Transformer の結果をそのまま使う
          if (node.rotation() === 0) {
            switch (activeAnchor) {
              case "top-left": // 左上ハンドル -> 右下固定
                newX = prevX + prevWidth - newWidth;
                newY = prevY + prevHeight - newHeight;
                break;
              case "top-right": // 右上ハンドル -> 左下固定
                newX = prevX; // Xは左端のまま
                newY = prevY + prevHeight - newHeight;
                break;
              case "bottom-left": // 左下ハンドル -> 右上固定
                newX = prevX + prevWidth - newWidth;
                newY = prevY; // Yは上端のまま
                break;
              case "bottom-right": // 右下ハンドル -> 左上固定
                newX = prevX; // Xは左端のまま
                newY = prevY; // Yは上端のまま
                break;
              // 辺のハンドル ("middle-left", "middle-right", "top-center", "bottom-center") が
              // 操作された場合は、Transformer が計算した座標 (node.x(), node.y()) を
              // そのまま使うか、中心基準で再計算するなどの方法がある。
              // ここでは default で何もしないので、node.x(), node.y() が使われる。
              default:
                break;
            }
          }
          // --- 座標計算ここまで ---

          // 最終的な座標をグリッドにスナップ
          const finalX = snapToGrid(newX);
          const finalY = snapToGrid(newY);

          // 新しい属性で状態を更新
          onChange({
            ...shapeProps,
            x: finalX,
            y: finalY,
            width: newWidth, // 計算済みの新しい幅
            height: newHeight, // 計算済みの新しい高さ
            rotation: node.rotation(), // rotation も Transformer で変更される可能性があるため更新
          });
        }}
      >
        {/* 部屋の矩形 */}
        <Rect
          width={width}
          height={height}
          fill={fill || "#fffaf0"} // Sidebar から渡される fill を使うか、デフォルト値
          stroke="gray"
          strokeWidth={1} // 枠線を少し細く
          shadowBlur={isSelected ? 5 : 0} // 選択されているときに少し影をつける（任意）
          shadowColor={isSelected ? "rgba(0,0,0,0.3)" : undefined}
        />

        {/* 浴室または階段で画像がある場合は画像を表示、それ以外はテキストを表示 */}
        {(name.startsWith("浴室") || name.startsWith("階段")) && imageToDisplay ? ( // ★ 条件に階段を追加
          <KonvaImage
            image={imageToDisplay}
            width={width}
            height={height}
            listening={false} // 画像自体はクリックイベントを拾わない
            perfectDrawEnabled={false}
          />
        ) : (
          <Text
            // 階段・浴室以外（画像表示対象外）の場合は名前と帖数を表示
            // 階段・浴室で画像がない場合も、このテキスト表示になる (階段なら名前のみ)
            text={
              name.startsWith("階段") || isFreeSize ? name : `${name}\n(${areaInJou.toFixed(1)}帖)`
            }
            fontSize={15} // フォントサイズ調整
            fill="dimgray" // 文字色調整
            width={width} // テキストエリアの幅を Rect と同じに
            height={height} // テキストエリアの高さを Rect と同じに
            align="center" // 水平中央揃え
            verticalAlign="middle" // 垂直中央揃え
            listening={false} // テキスト自体はクリックイベントを拾わないようにする
            perfectDrawEnabled={false} // パフォーマンス向上のため
            lineHeight={1.2} // 行間調整
            padding={5} // 内側の余白
          />
        )}
      </Group>

      {/* Transformer コンポーネント (選択されているときのみ表示) */}
      {isSelected && (
        <Transformer
          ref={trRef}
          // リサイズ時の最小幅・高さを制限する関数
          boundBoxFunc={(oldBox, newBox) => {
            if (
              Math.abs(newBox.width) < GRID_SIZE ||
              Math.abs(newBox.height) < GRID_SIZE
            ) {
              return oldBox; // 最小サイズ未満なら変形をキャンセル
            }
            return newBox;
          }}
          anchorSize={10} // ハンドルの大きさ
          rotateEnabled={false} // 回転を無効化
          // ★「階段」または「浴室」で始まる名前の部屋はリサイズ無効
          resizeEnabled={!shapeProps.name.startsWith("階段") && !shapeProps.name.startsWith("浴室")}
          keepRatio={false} // 縦横比は固定しない
          // ★ リサイズハンドルがドラッグされる際の座標をグリッドにスナップ
          anchorDragBoundFunc={(oldAnchorPos, newAnchorPos, e) => {
            return {
              x: snapToGrid(newAnchorPos.x),
              y: snapToGrid(newAnchorPos.y),
            };
          }}
          // 回転時のスナップ設定 (任意)
          // rotationSnapEnabled={true}
          // rotationSnaps={[0, 90, 180, 270]}
          // resizeEnabled={true} // リサイズはデフォルトで有効
        />
      )}
    </>
  );
};

export default Room;
